import angular from 'angular';
import addChangeSystemLoginDialogTemplate from './AddChangeSystemLoginDialog.template.html';
import CSMCommonWeb from 'csm-common-web';
/** Services */
import { SystemLoginServicesProvider } from '../../services/SystemLoginServices';

/*******************************************************************************
 * @ngdoc    directive
 * @restrict A
 * @name  addChangeSystemLoginDialog
 * @param entityId     ID of entity to be modified. Default=0
 * @param defineProps  defineProps is expected to be a function which will return
 *                     an object which has configuration properties defined  on
 *                     it. The properties defined on that object will be made
 *                     available for the addChangeSystemDialog to use. See the
 *                     defaults section below.
 * @param onSave       Expects a function to handle both addLogin and removeLogin
 *                     cases. See @return below for more info.
 * @return The addLogin and removeLogin are the two methods
 *         which return anything from this Dialog. The onSave
 *         method initially passed into the directive gets invoked
 *         with an object in the form of {save: bool}. True when
 *         a login was added, false when one was removed. The parameter
 *         you need to pass to your onSave function in the template is
 *         called response. grid.appscope.$ctrl.onSave(response)
 *******************************************************************************/
export default angular
  .module('csm.books.AddChangeSystemLoginDialog', [CSMCommonWeb])
  .directive('addChangeSystemLoginDialog', addChangeSystemLoginDialogDirective)
  .provider('SystemLoginServices', SystemLoginServicesProvider).name;

/*******************************************************************************
 * addChangeSystemLoginDialogDirective */
/**
 * Directive that opens the add/change system login dialog when clicked.
 *******************************************************************************/
function addChangeSystemLoginDialogDirective(ClickDialogDirective) {
  return new ClickDialogDirective({
    template: addChangeSystemLoginDialogTemplate,
    scope: { orgId: '<', entityId: '<', defineProps: '<', onSave: '&' },
    size: 'md',
    preloader: preload,
    controller: Controller
  });
}
addChangeSystemLoginDialogDirective.$inject = ['ClickDialogDirective'];

/*******************************************************************************
 * preload */
/**
 * Checks the credential level of the selected entity against the credential
 * level of the logged in user. If the selected entity has a higher credential
 * level, this dialog will stop opening.
 *******************************************************************************/
function preload($scope, BusyScreen, $q, BooksSvc, SERVER, Alert) {
  BusyScreen.setBusy(true);
  return BooksSvc.viewEntityCredentials($scope.entityId).then(function (resp) {
    BusyScreen.setBusy(false);
    $scope.entityCredentials = resp.data;
    /*----------------------------*/
    /* Individual being modified. */
    /*----------------------------*/
    var userRoleRank = resp.data.userRole ? resp.data.userRole.rank : 0;

    /*------------------------------------------------------*/
    /* Close the modal dialog to prevent user from editting */
    /* credentials of an individual with a higher rank.     */
    /*------------------------------------------------------*/
    if (userRoleRank > SERVER.currentUser.rank) {
      Alert.error(
        'This person has a higher security level than you are allowed to view.',
        'Restricted Access'
      );
      return $q.reject();
    }
  });
}
preload.$inject = ['$scope', 'BusyScreen', '$q', 'BooksSvc', 'SERVER', 'Alert'];

/*******************************************************************************
 * Controller */
/**
 * Controller for the add/change system login dialog.
 *******************************************************************************/
function Controller($injector, $scope) {
  var $svcs = { $ctrl: this, $globals: {}, $scope: $scope };
  $injector.invoke(initCtrl, null, Object.assign($svcs, { $svcs: $svcs }));
  $injector.invoke(initGlobals, null, $svcs);
}
Controller.$inject = ['$injector', '$scope'];

/*******************************************************************************
 * accountSelectorLabel */
/**
 * Returns a label to be displayed in the account selector when multiple items
 * have been selected.
 *******************************************************************************/
/* @ngInject */ function accountSelectorLabel($ctrl, SERVER) {
  const label = `{0} ${SERVER.BooksWebConstants.accountPluralLabel} Selected`;

  return function () {
    const allAccounts =
      $ctrl.selectedAccounts.length == $ctrl.dpAccounts.length;
    return label.format(allAccounts ? 'All' : 'Multiple');
  };
}

/*******************************************************************************
 * addLogin */
/**
 * Called by checkEntity to save login info. Assumes all validation has been
 * performed.
 *******************************************************************************/
/* @ngInject */ function addLogin(
  $ctrl,
  $globals,
  $scope,
  BusyScreen,
  BooksSvc,
  Alert
) {
  return function () {
    /*-------------------------------------------------------------*/
    /* If we are dealing with an ASB and the role's rank < 10,     */
    /* use the expiration date. Otherwise we want to send NaN,     */
    /* which will tell the system that this login will not expire. */
    /*-------------------------------------------------------------*/
    var selectedRoleRank = $globals.getCurrentRoleRank();
    var serializedDate =
      $ctrl.alwaysShowDate || selectedRoleRank < 10 ? $ctrl.expiresOn : NaN;
    var accountsList =
      $ctrl.selectedAccounts.length > 0
        ? $ctrl.selectedAccounts.join(',')
        : 'NONE';

    /*--------------------------------*/
    /* Save the entity's credentials. */
    /*--------------------------------*/
    BusyScreen.promise(
      BooksSvc.saveEntityCredentials(
        $ctrl.entityId,
        $ctrl.username,
        $ctrl.data.currentRoleID,
        accountsList,
        serializedDate
      ).then(
        function (resp) {
          /*-----------------------------------------*/
          /* Close the dialog and return the service */
          /* response object with the save flag set. */
          /*-----------------------------------------*/
          $ctrl.onSave({
            response: angular.extend({ save: true }, resp.data)
          });
          $scope.$dismiss();
        },

        /*-------------------*/
        /* Handle the error. */
        /*-------------------*/
        function (resp) {
          var errmsg = resp.parsedResponse.message();

          /*--------------------------------------*/
          /* Handle the "user name in use" error. */
          /*--------------------------------------*/
          if (resp.parsedResponse.code() == 54202)
            errmsg +=
              ' Please, use a different e-mail or try making it more unique (e.g. appending numerals to the e-mail).';

          /*--------------------*/
          /* Display the error. */
          /*--------------------*/
          Alert.error(errmsg);
        }
      )
    );
  };
}

/*******************************************************************************
 * checkEntity */
/**
 * Validates input and verifies the entity has a first name and a last name
 * before proceeding with saving the credentials. If none, displays the
 * AddChangeEntityDialog to allow the user to update the entity. Called when the
 * Ok button is clicked.
 *******************************************************************************/
/* @ngInject */ function checkEntity(
  $ctrl,
  $globals,
  AddChangeEntityDialog,
  AddChangeStudentDialog,
  Alert,
  BooksSvc,
  BusyScreen,
  Tools,
  SERVER
) {
  var mEntityMakerActive = false;
  return function () {
    /*------------------------------------*/
    /* Clear alerts and reset validators. */
    /*------------------------------------*/
    Alert.clear();
    $ctrl.valGroup.reset();

    /*------------------------*/
    /* Validate input fields. */
    /*------------------------*/
    if (!$ctrl.valGroup.validate()) return;

    /*-------------------------*/
    /* Get entity information. */
    /*-------------------------*/
    BusyScreen.promise(
      BooksSvc.getEntity($ctrl.entityId).then(function (resp) {
        var entity = resp.data;

        /*------------------------------------------*/
        /* Entity is missing a first name and/or    */
        /* last name. Prompt user to update entity. */
        /*------------------------------------------*/
        if (Tools.isEmpty(entity.firstName) || Tools.isEmpty(entity.lastName)) {
          Alert.confirm(
            'First and Last Names are required to update the login. Do you wish to do that now?',
            'Names required.'
          ).then(function () {
            /*--------------------------------------------------*/
            /* Get the correct dialog based on the entity type. */
            /*--------------------------------------------------*/
            var dialog;
            switch (entity.type) {
              case SERVER.BooksDBConstants.ET_STUDENT:
                dialog = AddChangeStudentDialog.show(entity.id);
                break;
              case SERVER.BooksDBConstants.ET_TEACHER:
              case SERVER.BooksDBConstants.ET_PARENT:
              case SERVER.BooksDBConstants.ET_GRANDPARENT:
              case SERVER.BooksDBConstants.ET_COMMUNITY_PARTNER:
              case SERVER.BooksDBConstants.ET_OTHER:
                dialog = AddChangeEntityDialog.show(entity.id, entity.type);
                break;
            }

            /*-----------------------------------------*/
            /* Display the dialog to manage selection. */
            /*-----------------------------------------*/
            mEntityMakerActive = true;
            dialog
              .then((result) => Alert.success('Successfully updated.'))
              .finally(() => (mEntityMakerActive = false));
          });
        } else {
          $globals.addLogin();
        }

        /*----------------------------------------*/
        /* Proceed with saving login information. */
        /*----------------------------------------*/
      })
    );
  };
}

/*******************************************************************************
 * defaultExpiration */
/**
 * Returns the default expiration date of 180 days from now.
 *******************************************************************************/
/* @ngInject */ function defaultExpiration() {
  //                 Days, Hours, Minutes, Seconds, Milliseconds.
  const timeOffset = 180 * 24 * 60 * 60 * 1000;

  return function () {
    var date = new Date();
    var time = date.valueOf();

    /*-----------------------------------------------------------*/
    /* Set the new time to 180 days from now using milliseconds. */
    /*-----------------------------------------------------------*/
    date.setTime(time + timeOffset);
    return date;
  };
}

/*******************************************************************************
 * getCurrentRoleRank */
/**
 * Returns the rank for the current selected role.
 *******************************************************************************/
/* @ngInject */ function getCurrentRoleRank($ctrl) {
  return function () {
    return (
      $ctrl.dpRoles
        .filter((role) => role.id == $ctrl.data.currentRoleID)
        .shift().rank || 0
    );
  };
}

/*******************************************************************************
 * includeDateSelector */
/**
 * Include the date selection in the panel. If it is a booster/pta, this will
 * check to see if there is a selected item and if the selected item's rank > 10.
 * Otherwise, if this is being loaded for an ASB, we always want to show the data
 * selector.
 *******************************************************************************/
/* @ngInject */ function includeDateSelector($ctrl, $globals) {
  return function () {
    return $ctrl.alwaysShowDate
      ? true
      : $ctrl.data.currentRoleID && $globals.getCurrentRoleRank() < 10;
  };
}

/*******************************************************************************
 * initCtrl */
/**
 * Initializes the $ctrl object.
 *******************************************************************************/
/* @ngInject */ function initCtrl(
  $ctrl,
  $injector,
  $scope,
  $svcs,
  SERVER,
  Tools
) {
  var accountReport =
    SERVER.BooksWebConstants.accountLabel == 'Category'
      ? 'Category History'
      : 'Account Statement';

  Object.assign(
    $ctrl,
    {
      /*-----------------------------------------------*/
      /* Default properties to be overwritten by the   */
      /* object returned from calling `definedProps()` */
      /* as the last parameter of `Object.assign`.     */
      /*-----------------------------------------------*/
      roles: [],
      alwaysShowDate: true,
      helpMsg: null,
      usernameIsEmail: true,
      includeRole: true,
      disableAccountSelector: false,

      entityCredentials: $scope.entityCredentials,
      expiresOn: null, // ExpiresOn
      selectedAccounts: [], // Selected Account IDs
      valGroup: {},
      data: { currentRoleID: null }, // Current Role
      accountLabel: SERVER.BooksWebConstants.accountLabel, // Account Label

      /*----------------------------*/
      /* Account instructional text */
      /*----------------------------*/
      accountText: `Choose ${SERVER.BooksWebConstants.accountPluralLabel} this user can view with the ${accountReport} Report`,

      /*----------------*/
      /* Data Providers */
      /*----------------*/
      dpRoles: [], // List of Roles.
      dpAccounts: [], // List of Accounts.

      /*-----------*/
      /* Accessors */
      /*-----------*/
      get displayHelpMsg() {
        return this.helpMsg != null;
      }, // Display Help Msg flag

      $onInit: $injector.invoke(onInit, null, $svcs),
      accountSelectorLabel: $injector.invoke(accountSelectorLabel, null, $svcs),
      checkEntity: $injector.invoke(checkEntity, null, $svcs),
      includeDateSelector: $injector.invoke(includeDateSelector, null, $svcs),
      removeLogin: $injector.invoke(removeLogin, null, $svcs),
      resetPassword: $injector.invoke(resetPassword, null, $svcs),
      unlockLogin: $injector.invoke(unlockLogin, null, $svcs),
      usernameLabel: $injector.invoke(usernameLabel, null, $svcs)
    },
    $ctrl.defineProps()
  );

  Object.defineProperty($ctrl, 'hasLogin', {
    get: function () {
      return (
        !Tools.isNaNZero(this.entityCredentials.loginID) &&
        !Tools.isNaNZero(this.data.currentRoleID)
      );
    }
  });
  Object.defineProperty($ctrl, 'isLocked', {
    get: function () {
      return this.entityCredentials.isLocked;
    }
  });
}

/*******************************************************************************
 * initGlobals */
/**
 * Initializes the $globals object with properties and functions not needed by
 * the html.
 *******************************************************************************/
/* @ngInject */ function initGlobals($globals, $injector, $svcs) {
  Object.assign($globals, {
    addLogin: $injector.invoke(addLogin, null, $svcs),
    defaultExpiration: $injector.invoke(defaultExpiration, null, $svcs),
    getCurrentRoleRank: $injector.invoke(getCurrentRoleRank, null, $svcs)
  });

  return $globals;
}

/*******************************************************************************
 * onInit */
/**
 * Load accounts and entities.
 *******************************************************************************/
/* @ngInject */ function onInit(
  $ctrl,
  $globals,
  BusyScreen,
  $q,
  Tools,
  SystemLoginServices
) {
  return function () {
    var svcCalls = [];
    svcCalls.push(SystemLoginServices.viewChartOfAccounts($ctrl.entityId));
    svcCalls.push(SystemLoginServices.getOrganizationRoles($ctrl.orgId));

    BusyScreen.setBusy(true);
    $q.all(svcCalls).then(function (resp) {
      /*-----------------------------------------------------*/
      /* Get accounts sorted alphabetically by account name. */
      /*-----------------------------------------------------*/
      $ctrl.dpAccounts = resp[0].sort(function (a, b) {
        if (a.accountName.toLowerCase() < b.accountName.toLowerCase())
          return -1;
        if (a.accountName.toLowerCase() > b.accountName.toLowerCase()) return 1;
        return 0;
      });

      /*-----------------------------------------------*/
      /* Set up the list of available roles to assign. */
      /* Restrict to provided roles if available.      */
      /*-----------------------------------------------*/
      $ctrl.dpRoles = resp[1]
        .filter(function (r) {
          return (
            $ctrl.roles == null ||
            $ctrl.roles.length == 0 ||
            $ctrl.roles.indexOf(r.role) > -1
          );
        })
        .sort(function (a, b) {
          if (a.role.toLowerCase() < b.role.toLowerCase()) return -1;
          if (a.role.toLowerCase() > b.role.toLowerCase()) return 1;
          return 0;
        });

      /*------------------------*/
      /* Get current user role. */
      /*------------------------*/
      var userRole = $ctrl.entityCredentials.userRole;

      /*-------------------------------------*/
      /* Set the login user name and get the */
      /* role if there is a current login.   */
      /*-------------------------------------*/
      $ctrl.username = $ctrl.entityCredentials.username;
      $ctrl.data.currentRoleID = resp[1]
        .filter(function (r) {
          return userRole && r.role == userRole.role;
        })
        .map(function (r) {
          return r.id;
        })
        .shift();
      $ctrl.expiresOn = new Date(
        Tools.coalesce(
          $ctrl.entityCredentials.expiresOn,
          $globals.defaultExpiration()
        )
      );

      /*--------------------------------------------*/
      /* Cycle through the chart of accounts to set */
      /* the permitted accounts for this entity.    */
      /*--------------------------------------------*/
      var permittedAccounts = $ctrl.entityCredentials.accounts;
      $ctrl.selectedAccounts = (permittedAccounts || []).map(function (pa) {
        return pa.accountID;
      });

      BusyScreen.setBusy(false);
    });
  };
}

/*******************************************************************************
 * removeLogin */
/**
 * Removes the login credential for the entity. The login ID will not be removed
 * so we can create new credentials using the same login ID.
 *
 * Removes the login and role.
 *******************************************************************************/
/* @ngInject */ function removeLogin(
  $ctrl,
  $scope,
  Alert,
  BusyScreen,
  BooksSvc
) {
  return function () {
    /*----------------------------------------------*/
    /* User does not have login. Nothing to remove. */
    /*----------------------------------------------*/
    if (!$ctrl.hasLogin) return;

    /*-----------------------*/
    /* Confirm login remove. */
    /*-----------------------*/
    Alert.confirm(
      'Are you sure you want to remove this user Login?',
      'Remove Login'
    ).then(function () {
      /*---------------------------------------------*/
      /* Remove login, notify user and close dialog. */
      /*---------------------------------------------*/
      BusyScreen.promise(
        BooksSvc.removeEntityLogin($ctrl.entityId).then(
          function (resp) {
            /*-------------------------------------*/
            /* Close the dialog with the save flag */
            /* on the return object set to false.  */
            /*-------------------------------------*/
            $ctrl.onSave({ response: { save: false } });
            $scope.$close();
          },

          /*-----------------------------*/
          /* Service call error handler. */
          /*-----------------------------*/
          function (resp) {
            Alert.error('Remove login error.');
          }
        )
      );
    });
  };
}

/*******************************************************************************
 * resetPassword */
/**
 * Sends a password reset to the user's email.
 *******************************************************************************/
/* @ngInject */ function resetPassword(
  $ctrl,
  Alert,
  BusyScreen,
  SystemLoginServices
) {
  return function () {
    Alert.confirm(
      'Are you sure you want to reset the password for this user?'
    ).then(() => {
      BusyScreen.setBusy(true);
      SystemLoginServices.resetPassword($ctrl.username)
        .then(() => {
          Alert.success(
            'An email has been sent to the user for password reset.',
            'Reset Password'
          );
          BusyScreen.setBusy(false);
        })
        .catch((resp) => {
          Alert.error(resp.parsedResponse.message(), 'Reset Password');
          BusyScreen.setBusy(false);
        });
    });
  };
}

/*******************************************************************************
 * unlockLogin */
/**
 * Unlocks the user's account by clearing the retry count.
 *******************************************************************************/
/* @ngInject */ function unlockLogin(
  $ctrl,
  Alert,
  BusyScreen,
  SystemLoginServices
) {
  return function () {
    Alert.confirm('Are you sure you want to unlock this user?').then(
      (o) => {
        BusyScreen.promise(
          SystemLoginServices.resetRetries(
            $ctrl.entityCredentials.loginID
          ).then(() => {
            $ctrl.entityCredentials.isLocked = false;
            Alert.success('The user has been unlocked.');
          })
        );
      },
      (resp) => Alert.error(resp.parsedResponse.message())
    );
  };
}

/*******************************************************************************
 * usernameLabel */
/**
 * Returns the label of the new username form item.
 *******************************************************************************/
/* @ngInject */ function usernameLabel($ctrl) {
  return function () {
    return `Username${$ctrl.usernameIsEmail ? ' (Email)' : ''}`;
  };
}
